"use client";

// Bloc clés du rail droit de /reps. 1 clé = 1 leçon (dépensée au démarrage).
// Quota du jour rechargé à minuit local -> décompte live. Illimité = abonné /
// onboarding (badge infini).
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { useSession } from "next-auth/react";
import Image from "next/image";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { hasUnlimitedEnergy } from "@/lib/reps/access";

// Icône clé = l'illustration maison key.svg (et non un logo Solar) : une vraie
// monnaie identifiable, cohérente avec la barre du haut et les coffres.
function KeyIcon({ size = 16, className = "" }) {
    return (
        <Image
            src="/static/icons/key.svg"
            width={size}
            height={Math.round(size * 1.11)}
            alt=""
            className={className}
        />
    );
}

function formatCountdown(targetIso) {
    if (!targetIso) return "";
    const ms = new Date(targetIso).getTime() - Date.now();
    if (ms <= 0) return "0s";
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}h ${String(m).padStart(2, "0")}m ${String(sec).padStart(2, "0")}s`;
}

export default function RepsKeyCard() {
    const t = useTranslations();
    const { data: session } = useSession();
    const sessionUnlimited = hasUnlimitedEnergy(session && session.user);
    const [state, setState] = useState(null);
    const [, setTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        function load() {
            fetch("/api/reps/keys-state", { method: "POST" })
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => {
                    if (!cancelled && d && d.keys) setState(d.keys);
                })
                .catch(() => {});
        }
        load();
        const onRefresh = () => load();
        window.addEventListener("focus", onRefresh);
        window.addEventListener("reps:keys-changed", onRefresh);
        return () => {
            cancelled = true;
            window.removeEventListener("focus", onRefresh);
            window.removeEventListener("reps:keys-changed", onRefresh);
        };
    }, []);

    // Tic chaque seconde pour le décompte.
    useEffect(() => {
        const id = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, []);

    const unlimited = state ? state.unlimited : sessionUnlimited;
    const dailyMax = state && state.dailyMax ? state.dailyMax : 3;
    const keys = state && !state.unlimited ? state.keys : dailyMax;
    const bonus = state && state.bonusKeys ? state.bonusKeys : 0;
    const starter = state && state.starterKeys ? state.starterKeys : 0;

    return (
        <div className="rounded-xl border border-gray-100 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
            <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white">
                    <KeyIcon size={16} />
                    {t("reps_keys_label")}
                </span>
                {unlimited ? (
                    <span
                        title={t("reps_keys_unlimited")}
                        className="flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-2.5 py-0.5 text-sm font-bold text-white shadow-sm"
                    >
                        <KeyIcon size={15} />
                        <SolarIcon name="infinity-bold" className="text-base" />
                    </span>
                ) : (
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-amber-600 dark:text-amber-400">
                        <KeyIcon size={16} /> {keys}
                        {starter > 0 || bonus > 0 ? "" : ` / ${dailyMax}`}
                    </span>
                )}
            </div>

            {!unlimited && starter === 0 && (
                <div className="mb-2 flex items-center gap-1.5">
                    {Array.from({ length: dailyMax }).map((_, i) => (
                        <KeyIcon
                            key={i}
                            size={18}
                            className={
                                i < keys ? "" : "opacity-25 grayscale"
                            }
                        />
                    ))}
                    {bonus > 0 && (
                        <span className="ml-1 flex items-center gap-0.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                            +{bonus}
                            <KeyIcon size={13} />
                        </span>
                    )}
                </div>
            )}

            {!unlimited && starter > 0 && state.starterExpiresAt ? (
                <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
                    {t("reps_keys_starter_expires", {
                        count: starter,
                        time: formatCountdown(state.starterExpiresAt),
                    })}
                </p>
            ) : (
                <p className="mt-1 text-xs text-gray-400">
                    {unlimited
                        ? // Accès offert (fiche admin) : ne pas prétendre que
                          // c'est "inclus dans l'abonnement".
                          state && state.unlimitedSource === "granted"
                            ? t("reps_keys_unlimited_caption_granted")
                            : t("reps_keys_unlimited_caption")
                        : state && state.nextResetAt
                          ? t("reps_keys_reset_in", {
                                time: formatCountdown(state.nextResetAt),
                            })
                          : t("reps_keys_caption")}
                </p>
            )}
        </div>
    );
}
